import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleLogo } from "@phosphor-icons/react";

const SIDE_IMG =
  "https://images.pexels.com/photos/7964562/pexels-photo-7964562.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export function roleHome(role) {
  if (role === "employer") return "/employer";
  if (role === "recruiter") return "/recruiter";
  return "/dashboard";
}

export function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F9FAFB]">
      <div className="hidden lg:block relative">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-sm bg-white flex items-center justify-center text-slate-900 font-heading font-black text-sm">E</span>
            <span className="font-heading font-bold text-lg">Eligvia</span>
          </Link>
          <div>
            <p className="font-mono-num text-xs uppercase tracking-[0.2em] text-slate-300 mb-4">Eligibility first</p>
            <h2 className="font-heading font-black text-3xl leading-tight max-w-sm">Find jobs you can actually apply for — and know why.</h2>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <span className="h-7 w-7 rounded-sm bg-slate-900 flex items-center justify-center text-white font-heading font-black text-sm">E</span>
            <span className="font-heading font-bold text-lg">Eligvia</span>
          </div>
          <h1 className="font-heading font-bold text-2xl tracking-tight">{title}</h1>
          <p className="mt-1.5 text-sm text-slate-600">{subtitle}</p>
          <div className="mt-8">{children}</div>
          <div className="mt-6 text-sm text-slate-600 text-center">{footer}</div>
        </div>
      </div>
    </div>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export function GoogleButton({ label }) {
  return (
    <Button type="button" variant="outline" onClick={googleLogin} data-testid="google-auth-btn"
      className="w-full border-slate-300 gap-2">
      <GoogleLogo weight="bold" /> {label}
    </Button>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(roleHome(u.role));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Sign in" subtitle="Welcome back. Continue your job search."
      footer={<>Don't have an account? <Link to="/register" className="font-semibold text-slate-900 underline">Create one</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input" className="mt-1.5" placeholder="you@example.com" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-slate-900 underline" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input" className="mt-1.5" placeholder="••••••••" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full bg-slate-900 hover:bg-slate-800">
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
        <div className="h-px flex-1 bg-slate-200" /> OR <div className="h-px flex-1 bg-slate-200" />
      </div>
      <GoogleButton label="Continue with Google" />
      {/preview\.emergentagent\.com|localhost/.test(window.location.hostname) && (
        <p className="mt-6 text-xs text-slate-500 text-center" data-testid="login-demo-hint">Demo: candidate@matchwell.io · Candidate!2345</p>
      )}
    </AuthShell>
  );
}
